/**
 * Price books (subscription engine, section 16): what customers pay, as
 * immutable versions. Publishing takes effect without a deploy: the next
 * bills raised and the public pricing page both read the book in force.
 */
import { api } from '@lipaflow/shared'
import type { PriceBand } from '@lipaflow/shared/pricing'

export interface PriceBook {
  id: string
  version: number
  /** The product priced: a school product, or the vertical's own code. */
  product_code: string
  /** A bundle's book: its discount off its parts bought apart (0.2 is 20%).
   *  It has no bands. Null for a product priced by bands. */
  bundle_discount: number | null
  name: string
  status: 'draft' | 'published' | 'archived'
  currency: string
  effective_from: string
  published_at: string | null
  cadence_discounts: Record<string, number>
  min_charge: number
  band_pricing: 'graduated' | 'volume'
  bands: (PriceBand & { name: string | null })[]
}

export interface PriceBookList {
  vertical: string
  unit: string | null
  period: string | null
  /** The default product's book in force. */
  in_force: string | null
  /** Where a vertical sells several products (schools), each with its own
   *  prices. Empty: one product, coded like the vertical. */
  products: { code: string; name: string; parts: string[]; in_force: string | null }[]
  price_books: PriceBook[]
}

export interface NewPriceBook {
  name: string
  product_code?: string
  /** A bundle's book only: its discount, with no bands and no minimum. */
  bundle_discount?: number
  bands: { from_qty: number; rate: number | null }[]
  min_charge: number
  cadence_discounts: Record<string, number>
  band_pricing: 'graduated' | 'volume'
  /** Omitted: now, by the server's clock. */
  effective_from?: string
  publish: boolean
}

const base = (verticalId: string) => `/admin/verticals/${verticalId}/price-books`

export const priceBookService = {
  async list(verticalId: string): Promise<PriceBookList> {
    return (await api.get<PriceBookList>(base(verticalId))).data
  },
  async create(verticalId: string, book: NewPriceBook): Promise<PriceBook> {
    return (await api.post<PriceBook>(base(verticalId), book)).data
  },
  async publish(verticalId: string, bookId: string, startNow = false): Promise<PriceBook> {
    return (await api.post<PriceBook>(`${base(verticalId)}/${bookId}/publish`, { start_now: startNow })).data
  },
  async archive(verticalId: string, bookId: string): Promise<PriceBook> {
    return (await api.post<PriceBook>(`${base(verticalId)}/${bookId}/archive`)).data
  },
}
